import { Injectable, computed, inject, signal } from '@angular/core';
import { AccountSettingsService, ICustomerProfile } from '../../customers-onboarding/services/account-settings.service';

/**
 * Caches the signed-in customer's saved profile (name / phone) so the header,
 * dashboard and account settings all show the same values.
 *
 * B2C local-account tokens rarely carry given_name/family_name, so components
 * must not rely on claims alone — they read the name from here, which is loaded
 * once from GET customer/account/profile.
 */
@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  private readonly api = inject(AccountSettingsService);

  private readonly _profile = signal<ICustomerProfile | null>(null);
  private loaded = false;

  readonly profile = this._profile.asReadonly();
  readonly firstName = computed(() => this._profile()?.firstName ?? '');
  readonly lastName = computed(() => this._profile()?.lastName ?? '');
  readonly companyName = computed(() => this._profile()?.companyName ?? '');
  /** clientType: 1 = Business, 0 = Individual. */
  readonly isBusiness = computed(() => this._profile()?.clientType === 1);
  readonly fullName = computed(() =>
    [this.firstName(), this.lastName()].filter(Boolean).join(' ').trim(),
  );
  /**
   * The name to show for this customer: the company name for a business account,
   * otherwise their personal name. Empty string if nothing is known yet.
   */
  readonly displayName = computed(() =>
    this.isBusiness() ? (this.companyName() || this.fullName()) : (this.fullName() || this.companyName()),
  );

  /** Fetch the profile once (subsequent calls are no-ops unless forced). */
  load(force = false): void {
    if (this.loaded && !force) return;
    this.loaded = true;
    this.api.getProfile().subscribe({
      next: (p) => this._profile.set(p),
      error: () => {
        // Not a customer / not onboarded yet — components fall back to claims.
        this.loaded = false;
      },
    });
  }

  /** Update the cache after the user edits their profile. */
  set(profile: ICustomerProfile): void {
    this._profile.set(profile);
    this.loaded = true;
  }
}
